<template>
	<el-card class="patientSettingsCard">
		<div slot="header" class="header settings" style="padding-left: 0;">
			<SettingIcon></SettingIcon>
			<span style="margin-left: 12px;"><strong>Settings</strong></span>
		</div>
		<el-form ref="form" label-width="150px" label-position="top" style="padding:10px 20px 8px; width: 60%">
			<el-form-item label="Full Name">
				<el-row>
					<el-col :span="19">
						<el-input
							v-model="setting.name"
							size="small"
							placeholder="Full Name"
							:class="nameErrorText ? 'input-error' : ''">
						</el-input>
						<span v-if="nameErrorText" class="error-text">
							{{ nameErrorText }}
						</span>
					</el-col>
				</el-row>
			</el-form-item>
			<el-form-item label="Email Address">
				<el-row>
					<el-col :span="19">
						<el-input
							v-model="setting.email"
							size="small"
							placeholder="name@example.com"
							:class="patient.email && (!isEmailVerified || emailErrorText) && !saving ? 'input-error' : ''"
							:disabled="isEmailVerified">
							<template
								slot="suffix"
								v-if="isEmailVerified">
									<i class="el-icon-success" style="color: #44b358"></i>
									<span class="email-verified-suffix success">Verified</span>
							</template>
							<template
								slot="suffix"
								v-if="isEmailInvalid && !saving">
									<i class="el-icon-warning" style="color: #da4e4e"></i>
									<span class="email-verified-suffix warning">Invalid</span>
							</template>
							<template
								slot="suffix"
								v-else-if="isEmailUnverified && !saving">
									<i class="el-icon-warning" style="color: #da4e4e"></i>
									<span class="email-verified-suffix warning">Unverified</span>
							</template>
						</el-input>
						<span v-if="isEmailInvalid && !saving" class="error-text">
							This email address seems to be incorrect
						</span>
						<span v-else-if="isEmailUnverified && !saving" class="error-text">
							Patient has not verified / confirmed their email address
						</span>
						<span v-else-if="emailErrorText" class="error-text">
							{{ emailErrorText }}
						</span>
					</el-col>
					<el-col :span="5" v-if="isEmailUnverified && validEmail(setting.email)">
						<el-button type="text" size="small"
									class="send-now"
									v-if="!resent"
									:disabled="resending"
									@click="resendVerification">
							Remind now
						</el-button>
						<a v-if="resending" class="icon-wrapper">
							<LoadSpinnerIcon type="primary"></LoadSpinnerIcon>
						</a>
						<a v-if="resent" class="reminderSent">
							<i class="el-icon-check"></i>
							Reminder sent
						</a>
					</el-col>
				</el-row>
			</el-form-item>
			<el-form-item label="Phone No.">
				<el-row>
					<el-col :span="19">
						<el-input
							v-model="setting.phone"
							size="small"
							placeholder="(123) 456-7890"
							:class="patient.phone && (!isPhoneVerified || phoneErrorText) && !saving ? 'input-error' : ''"
							:disabled="isPhoneVerified">
							<template
								slot="suffix"
								v-if="isPhoneVerified">
									<i class="el-icon-success" style="color: #44b358"></i>
									<span class="email-verified-suffix success">Verified</span>
							</template>
							<template
								slot="suffix"
								v-else-if="isPhoneUnverified && !saving">
									<i class="el-icon-warning" style="color: #da4e4e"></i>
									<span class="email-verified-suffix warning">Unverified</span>
							</template>
						</el-input>
						<span v-if="phoneErrorText" class="error-text">
							{{ phoneErrorText }}
						</span>
						<span v-else-if="isPhoneUnverified && !saving" class="error-text">
							Patient has not verified / confirmed their phone number
						</span>
					</el-col>
				</el-row>
			</el-form-item>
			<el-form-item>
				<template>
					Push Notification
					<el-switch
						v-model="setting.push_enabled"
						:disabled="!isAppDownloaded">
					</el-switch>
					<span v-if="!isAppDownloaded" class="warning-text">App not downloaded</span>
					<div v-if="!isAppDownloaded" class="oval"></div>
					<el-button type="text" size="small"
								class="send-now"
								v-if="!isAppDownloaded"
								@click="openSendNowModal($event)">
						Remind now
					</el-button>
				</template>
			</el-form-item>
			<el-form-item v-if="isEmailVerified && validEmail(setting.email)">
				<template>
					Email Notification
					<el-switch					
						v-model="setting.email_enabled">
					</el-switch>
				</template>
			</el-form-item>
			<el-form-item>
				<el-row>
					<el-button type="primary" @click="saveChanges"
						v-bind:disabled="!hasUpdate || saving">
						<template v-if="saving">
							<a class="save-icon-wrapper">
								<LoadSpinnerIcon type="default"></LoadSpinnerIcon>
							</a>
						</template>
						<template v-else>
							<span class="save">Save Changes</span>
						</template>
					</el-button>
					<a v-if="saved" class="reminderSent">
						<i class="el-icon-check"></i>
						Changes saved
					</a>
				</el-row>
			</el-form-item>
		</el-form>
		<el-dialog
				:visible.sync="showSendNowModal"
				:append-to-body="true"
				class="send-now-modal"
				:close-on-click-modal="false"
				:before-close="finishSendingReminder"
				:width="isReminderSent ? '410px' : '630px'">
			<span slot="title">Reminder to Download DoctorPlan App</span>
			<template v-if="!isReminderSent">
				<p>Send to</p><br>
				<el-row>
					<el-col :span="5" type="flex">
						<el-checkbox v-model="sendEmail" class="checkbox">Email</el-checkbox>
					</el-col>
					<el-col :span="19">
						<input
							placeholder=""
							v-model="patientEmail"
							:disabled="true">
					</el-col>
				</el-row>

				<el-row>
					<el-col :span="5">
						<el-checkbox v-model="sendSMS" class="checkbox">SMS</el-checkbox>
					</el-col>
					<el-col :span="19">
						<input placeholder="(123) 456-7890" v-model="patientPhone" :style="sendSMS && !validPhone(patientPhone) ? 'border: solid 1px #da4e4e' : ''">
						<span v-if="sendSMS && !validPhone(patientPhone)" class="error-text">
							Please enter valid mobile number
						</span>
					</el-col>
				</el-row>

				<span v-if="!sendSMS && !sendEmail" style="margin-top: 8px;" class="error-text">{{ reminderErrorText }}</span>

				<p class="info-box">This app is specially setup for {{getProviderName}}'s practice - everything is specific to your doctor, your condition and treatment plan. It also helps you prepare for appointments and sends you reminders, so you don't forget something you wanted to discuss with your doctor.</p>

				<div class="footer">
					<LoadingButton
							type="primary"
							size="small"
							class="send-button"
							@click="sendReminder"
							:loading="sendingReminder"
							:loaded="isReminderSent">
						Send
					</LoadingButton>
					<el-button type="text" size="small" class="cancel-button" @click="showSendNowModal = false">
						Cancel
					</el-button>
				</div>
			</template>
			<div v-else class="send-succcess">
				<TickIcon></TickIcon>
				<span v-if="reminderErrorText.length">{{ reminderErrorText }}</span>
				<span v-else-if="sendEmail && !sendSMS">Reminder to Download DoctorPlan App sent by Email.</span>
				<span v-else-if="sendEmail && sendSMS">Reminder to Download DoctorPlan App sent by Email and SMS.</span>
				<span v-else-if="!sendEmail && sendSMS">Reminder to Download DoctorPlan App sent by SMS.</span>
				<div class="footer">
					<el-button type="primary" size="small" style="margin-left: 15px;" @click="finishSendingReminder">Done</el-button>
				</div>
			</div>
		</el-dialog>
	</el-card>
</template>

<script>
import { mapActions, mapGetters } from 'vuex'
import OverviewCard from '..'

import validEmail from '@/module/util/valid-email'
import validPhone from '@/module/util/valid-phone'
import TickIcon from '../../icon/Tick'
import Icon from '../../icon'
import LoadSpinnerIcon from '@/module/component/icon/LoadSpinner'
import LoadingButton from '@/module/component/LoadingButton'

export default {
	name: 'patient-settings-card',
	extends: OverviewCard,

	data() {
		return {
			setting: {
				name: this.patient.name,
				email: this.patient.email,
				phone: this.patient.phone,
				push_enabled: this.patient.hasOwnProperty('push_disabled') ? !this.patient.push_disabled : true,
				email_enabled: this.patient.hasOwnProperty('email_disabled') ? !this.patient.email_disabled : true,
			},
			verified: this.patient.verified,
			phoneVerified: this.patient.hasOwnProperty('phone_verified') ? this.patient.phone_verified : false,
			email_bounce: this.patient.email_bounce,
			saving: false,
			saved: false,
			resending: false,
			resent: false,
			showSendNowModal: false,
			reminderErrorText: '',
			isReminderSent: false,
			sendingReminder: false,
			sendEmail: !!this.patient.email,
			sendSMS: !!this.patient.phone,
			patientEmail: this.patient.email,
			patientPhone: this.patient.phone,
			phoneErrorText: '',
			emailErrorText: '',
			nameErrorText: '',
		}
	},

	methods: {
		...mapActions('provider', [
			'updatePatient',
			'resendVerify',
			'notifyPatient',
		]),

		validEmail,
		validPhone,

		saveChanges() {
			if (this.hasUpdate) {
				if (this.isFormValid()) {
					this.saving = true
					this.updatePatient(Object.assign({
						id: this.patient.id,
						provider: this.requestProvider
					}, this.settingsUpdate))
					.then(response => {
						// updating DOM values
						this.setting = {
							name: response.name,
							email: response.email,
							phone: response.phone,
							push_enabled: !response.push_disabled,
							email_enabled: !response.email_disabled,
						}
						this.verified = response.verified
						this.email_bounce = response.email_bounce

						this.saving = false
						this.saved = true
						setTimeout(() => { this.saved = false }, 3000)
					})
					.catch((error) => {
						this.saving = false
						if (error.status = '422' && error.code === 'EmailExists') {
							this.emailErrorText = 'This email already exists'
						}
						console.log('save changes error', error)
					})
				}
			}
		},

		isFormValid() {
			// check for name validation
			this.setting.name.trim().indexOf(' ') > 0 ? this.nameErrorText = "" : this.nameErrorText = "Enter the patient's first and last name"

			// check for email validation
			if (this.setting.email)
				!this.validEmail(this.setting.email) ? this.emailErrorText = "Enter a valid email address" : this.emailErrorText = ""
			
			// check for phone validation
			this.setting.phone && !this.validPhone(this.setting.phone) ? this.phoneErrorText = "Enter valid phone number" : this.phoneErrorText = ""

			if (this.nameErrorText || this.emailErrorText || this.phoneErrorText)
				return false
			else 
				return true
		},

		resendVerification() {
			if (this.resending)
				return

			this.resending = true
			this.resendVerify({ id: this.patient.id, provider: this.requestProvider })
				.then(() => {
					console.log('finished resend')
					this.resending = false
					this.resent = true
					setTimeout(() => { this.resent = false }, 3000)
				})
				.catch((error) => {
					console.log('resend error', error)
					this.resending = false
				})
		},

		finishSendingReminder() {
			this.reminderErrorText = ''
			this.isReminderSent = false
			this.showSendNowModal = false
		},

		isReminderValid() {
			this.reminderErrorText = ''
			if (!this.sendEmail && !this.sendSMS) {
				this.reminderErrorText = 'Select Email or SMS check-box to send the reminder'
				return false
			}
			else if (this.sendEmail && !this.validEmail(this.patientEmail))
				return false
			else if (this.sendSMS && !this.validPhone(this.patientPhone))
				return false
			return true
		},

		openSendNowModal(e) {
			e.stopPropagation()
			this.showSendNowModal = true
		},

		sendReminder() {
			if (!this.isReminderValid())
				return
			this.sendingReminder = true
			
			let reminderObject = {
				patient: this.patient.id,
				type: 'reminder-download'
			}
			
            if(this.sendEmail)
                reminderObject.email = this.patientEmail
            if(this.sendSMS)
				reminderObject.phone = this.patientPhone
			if(this.requestProvider)
				reminderObject.provider = this.requestProvider

			this.notifyPatient(reminderObject)
					.then(res => {
						this.sendingReminder = false
						this.isReminderSent = true
					})
					.catch(err => {
						this.sendingReminder = false
						this.reminderErrorText = 'Error sending reminder'
						console.log(this.reminderErrorText, err)
					})
		},
	},

	computed: {
		...mapGetters('provider', [
			'getProviderName',
		]),

		hasUpdate() {
			return Object.keys(this.settingsUpdate).length > 0
		},

		settingsUpdate() {
			var update = {}

			// Compare settings and patient
			if (this.setting.name != this.patient.name)
				update.name = this.setting.name.trim()

			// Check contact information
			if (this.setting.email !== this.patient.email)
				update.email = this.setting.email
			if (this.setting.phone !== this.patient.phone)
				update.phone = this.setting.phone
			if (this.setting.email_enabled !== !this.patient.email_disabled)
				update.email_disabled = !this.setting.email_enabled
			if (this.setting.push_enabled !== !this.patient.push_disabled)
				update.push_disabled = !this.setting.push_enabled

			return update
		},

		isAppDownloaded() {
			return this.patient.ios_version || this.patient.android_version
		},

		isPhoneVerified() {
			return this.phoneVerified && !this.phoneErrorText
		},

		isPhoneUnverified() {
			return this.patient.phone && this.patient.phone.length && !this.phoneVerified && !this.phoneErrorText
		},

		isEmailVerified() {
			return this.verified && !this.emailErrorText
		},

		isEmailInvalid() {
			return !this.verified && this.email_bounce && !this.emailErrorText
		},

		isEmailUnverified() {
			return this.patient.email && this.patient.email.length && !this.verified && !this.email_bounce && !this.emailErrorText
		},
		/**
		 * The provider to be used for api 
		 * calling for changes
		 */
		requestProvider() {
			if(this.provider)
				return this.provider.id
			else if(this.patientProviderProfileList.length > 0)
				return this.patientProviderProfileList[0].provider
			return null

		}
	},

	components: {
		LoadSpinnerIcon,
		LoadingButton,
		TickIcon,
		...Icon
	}
}
</script>

<style lang="scss" scoped>
	@import "@/module/app/theme/color.scss";
	.send-now, .send-now:hover {
		font-size: 12px;
		font-weight: 700;
		color: #2d3e4f;
		text-decoration: underline;
		margin: 0 9px;
	}

	.warning-text {
		font-size: 12px;
		font-weight: 500;
		color: $danger-color;
		margin: 0 8px;
	}

	.oval {
		height: 3px;
		width: 3px;
		margin-bottom: 2px;
		background-color: #2d3e4f;
		border-radius: 50%;
		display: inline-block;
	}

	.email-verified-suffix {
		font-size: 12px;
		font-weight: 500;
		margin: 0 6px;
	}
	.email-verified-suffix.success {
		color: #44b358;
	}

	.email-verified-suffix.warning {
		color: $danger-color;
	}
	.reminderSent {
		color: #44b358;
		margin-left: 14px;
		font-size: 12px;
		font-weight: 500;
	}
	.icon-wrapper {
		width: 10px;
		height: 10px;
		display: inline-block;
	}
	.save-icon-wrapper {
		width: 13px;
		height: 13px;
		display: inline-block;
		margin: 0 45px;
	}
	.error-text {
		color: $danger-color;
		margin-top: 5px;
		display: block;
		font-size: 12px;
		line-height: 1;
		font-weight: 500;
	}
	.input-error {
		border: 1px solid rgb(218, 78, 78);
		height: 38px;
		line-height: 35px;
		border-radius: 3px;
	}
	input:disabled {
		cursor: not-allowed;
		background-color: #fff !important;
	}
	.save {
		font-size: 14px;
		font-weight: 500;
	}
	.send-now-modal {
		margin-top: 70px;
		p {
			color: #333333;
		}
		.send-succcess {
			position: relative;
			word-break: break-word;
			span {
				position: absolute;
				top: 5px;
				left: 30px;
			}
		}
		.el-row {
			margin-top: 10px;
			.checkbox {
				margin-top: 5px;
				color: #333333;
			}
			input {
				width: 100%;
				height: 28px;
				border-radius: 0;
				border: solid 1px #cccccc;
				padding-left: 10px;

				font-size: 14px;
				font-weight: 500;
				font-stretch: normal;
				font-style: normal;
				line-height: 1.36;
				letter-spacing: 1px;
				color: #333333;
			}
			input:focus {
				outline: none;
				border: solid 1px #2d3e4f;
			}
			input::placeholder {
				color: #dddddd;
				font-weight: 400;
			}
		}
		.info-box {
			border: solid 1px #eeeeee;
			background-color: #f8f8f8;
			margin-top: 16px;
			margin-bottom: 40px;
			padding: 12px;
			font-size: 14px;
			line-height: 1.29;
			color: #333333;
			word-break: break-word;
		}
		.footer {
			display: block;
			height: 20px;
			margin-top: 20px;
			.send-button {
				margin-left: 10px;
				width: 66px;
				height: 35px;
				padding: 5px;
				border-radius: 0;
			}
			.el-button {
				float: right;
				font-size: 14px;
			}
			.cancel-button {
				text-decoration: underline;
				color: #333333;
			}
		}
	}

</style>

<style scoped>
	.patientSettingsCard >>> .el-card__body {
		padding-top: 30px;
	}
	.patientSettingsCard >>> .el-form--label-top .el-form-item__label {
		line-height: 1;
		padding-bottom: 4px;
		font-size: 12px;
		color: #666;
	}
	.patientSettingsCard >>> .el-form-item {
		margin-bottom: 20px;
	}
	.patientSettingsCard >>> .el-input__inner {
		font-size: 14px;
		color: #333;
		font-weight: 500;
	}
	.patientSettingsCard >>> .el-input.is-disabled input {
		color: #333;
		font-weight: 500;
	}
	.patientSettingsCard >>> .el-input.is-disabled .el-input__inner {
		background-color: #fff;
	}
	.patientSettingsCard >>> .el-switch {
		margin-left: 15px;
	}

	/* customizing input placeholder */
	.patientSettingsCard >>> ::-webkit-input-placeholder { /* Chrome/Opera/Safari */
		font-size: 12px;
	}
	.patientSettingsCard >>> ::-moz-placeholder { /* Firefox 19+ */
		font-size: 14px;
	}
	.patientSettingsCard >>> :-ms-input-placeholder { /* IE 10+ */
		font-size: 14px;
	}
	.patientSettingsCard >>> :-moz-placeholder { /* Firefox 18- */
		font-size: 14px;
	}
	.patientSettingsCard >>> .el-button--primary {
		border-radius: 0px;
	}
	.patientSettingsCard >>> .el-input__suffix-inner i {
		vertical-align: middle;
	}
	.input-error >>> .el-input__inner {
		border: none;
	}
</style>

<style lang="scss" scoped>
@import "../style.scss";
</style>
